import { Link } from "react-router-dom";
import { ArrowRight, Database, FileCheck2, FolderKanban, ShieldCheck } from "lucide-react";
import TechPreview from "../../components/marketing/TechPreview";
import Breadcrumbs from "../../components/marketing/Breadcrumbs";
import { useSeo } from "../../components/marketing/Seo";
import { IMAGES, TECH_PILLARS } from "../../data/marketing";

const icons = [FolderKanban, Database, ShieldCheck, FileCheck2];

export default function TechnologyPage() {
  useSeo({ title: "Technology", description: "Explore the CLYMACO client technology concept for obligations, deadlines, material records, workflows, evidence and reporting.", path: "/technology" });
  return (
    <main id="main" className="bg-parchment-base pt-36" data-testid="technology-page">
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6">
        <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Technology" }]} />
        <div className="max-w-3xl">
          <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-forest-primary">Our proprietary client technology</div>
          <h1 className="mt-4 font-display text-5xl font-medium tracking-tight text-forest-deep sm:text-6xl">Consulting sets the direction. Software keeps it connected.</h1>
          <p className="mt-5 text-lg leading-relaxed text-forest-deep/65">CLYMACO is a consulting + SaaS company. Our technology layer brings the advice, data, workflows and evidence of an engagement into a shared client workspace.</p>
        </div>
        <div className="mt-10 overflow-hidden rounded-[2rem] bg-forest-deep shadow-xl shadow-forest-deep/15">
          <img src={IMAGES.technology} alt="Systematic inventory shelving with barcode tracking infrastructure for waste batch verification" className="h-[320px] w-full object-cover opacity-85" />
          <div className="border-t border-white/10 bg-forest-deep px-5 py-3 font-mono text-[10px] uppercase tracking-widest text-emerald-100/60">Illustrative traceability infrastructure</div>
        </div>
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {TECH_PILLARS.map((pillar, index) => {
            const Icon = icons[index];
            return (
              <div key={pillar.num} className="rounded-3xl border border-forest-deep/10 bg-white p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-forest-primary/10 text-forest-primary"><Icon className="h-5 w-5" /></span>
                <h2 className="mt-5 font-display text-xl font-medium text-forest-deep">{pillar.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-forest-deep/60">{pillar.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section id="epr" className="bg-[#F4EFE6] py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="mb-8 max-w-3xl">
            <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-forest-primary">Interactive product concept</div>
            <h2 className="mt-3 font-display text-3xl font-medium text-forest-deep sm:text-4xl">Switch between EPR, BWG and CSR views.</h2>
            <p className="mt-4 text-base leading-relaxed text-forest-deep/65">Illustrative dashboard data and proposed workflows are shown to explain the product direction. Verified capabilities are scoped to each client engagement.</p>
          </div>
          <TechPreview />
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-8 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24">
        <div className="rounded-3xl bg-forest-deep p-8 text-[#FBF9F4] sm:p-10">
          <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-300">Consulting role</div>
          <h2 className="mt-3 font-display text-3xl font-medium">Judgement for the decisions.</h2>
          <p className="mt-4 text-base leading-relaxed text-emerald-100/70">Interpret obligations, design programmes, evaluate service providers and resolve the issues that need specialist input.</p>
        </div>
        <div className="rounded-3xl border border-forest-deep/10 bg-white p-8 sm:p-10">
          <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-forest-primary">SaaS role</div>
          <h2 className="mt-3 font-display text-3xl font-medium text-forest-deep">Continuity for the work.</h2>
          <p className="mt-4 text-base leading-relaxed text-forest-deep/65">Bring records, responsibilities and progress together so your team can act on the advice and follow it through.</p>
        </div>
      </section>

      <section id="walkthrough" className="bg-forest-deep py-16 text-center text-[#FBF9F4] lg:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-300">Product walkthrough</div>
          <h2 className="mt-3 font-display text-3xl font-medium sm:text-5xl">See how consulting and SaaS would fit your programme.</h2>
          <p className="mt-5 text-base leading-relaxed text-emerald-100/70">Tell us which workflow you want to review: EPR obligations, bulk waste records, CSR monitoring or a mandate across several practices.</p>
          <Link to="/contact?topic=Client%20technology%20%2F%20SaaS%20walkthrough" className="mt-8 inline-flex items-center gap-2 rounded-full bg-emerald-400 px-7 py-3.5 font-display text-sm font-bold text-forest-deep transition-colors hover:bg-emerald-300" data-testid="technology-walkthrough-cta">
            Arrange a product walkthrough <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </main>
  );
}
